/**
 * @file INCSUBNavbar.js
 * @module INCSUBNavbar
 * @description
 * This React component renders the top navigation bar for the INCSUB Admin Panel.
 * It displays the application logo, user profile information, and a notification icon with a dropdown panel.
 * 
 * Key Features:
 * - Shows user profile picture, name, and role (fetched from localStorage).
 * - Clicking the profile area navigates to the personal details view and updates Redux state accordingly.
 * - Notification icon toggles a dropdown panel for user notifications.
 * - Closes the notification panel when clicking outside.
 * 
 * Dependencies:
 * - React, Redux (useSelector, useDispatch)
 * - LocalStorage & SessionStorage for user data and navigation state
 * - Custom Redux slices: `commonSlice` and `userSlice`
 * - Child component: `INCSUBNotification`
 * - CSS Module: `styleNavbar.css`
 * 
 * @author govind dindewad

 */

import React, { useEffect, useRef, useState } from 'react';
import './styleNavbar.css';
import INCSUBNotification from '../notification/notification';
import { useDispatch, useSelector } from 'react-redux';
import store from '../../redux/Strore';
import { setRouteStatus } from '../../redux/commonSlice';
import { setProfileInfo, setSelectedUser, setUsrDetailsRoute } from '../../features/users/userSlice';

function INCSUBNavbar() {
  const profileInfo = useSelector((state)=>state.user.profileInfo);
  const [notifiPanel, setNotifiPanel] = useState(false);

  const notifiRef = useRef(null);
  const notificationIconRef = useRef(null);
  const routeStatus = useSelector((state) => state.common.routeStatus);
  const dispatch = useDispatch();

  /**
   * @function useEffect - Close Notification Panel
   * @description
   * Detects clicks outside the notification icon and panel area to automatically
   * close the notification dropdown if it is open.
   *
   * - Adds a global `mousedown` event listener on mount.
   * - Cleans up the listener on unmount to prevent memory leaks.
   *
   * @dependencies
   * - `notifiRef`: Reference to the notification dropdown panel.
   * - `notificationIconRef`: Reference to the notification icon.
   * - `setNotifiPanel`: State setter to toggle visibility.
   */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notifiRef.current &&
        !notifiRef.current.contains(event.target) &&
        notificationIconRef.current &&
        !notificationIconRef.current.contains(event.target)
      ) {
        setNotifiPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleUpdate = () => {
      const updated = localStorage.getItem('userData');
      if (updated) {
        dispatch(setProfileInfo(JSON.parse(updated)));
      }
    };

    window.addEventListener('userDataUpdated', handleUpdate);
    handleUpdate(); // Initial load

    return () => {
      window.removeEventListener('userDataUpdated', handleUpdate);
    };
  }, []);
  /**
  * @function handleProfileClick
  * @description
  * Handles click on the user profile section.
  * 
  * - Resets global Redux state.
  * - Retrieves user data from localStorage.
  * - Updates Redux store with the selected user.
  * - Stores new route in sessionStorage.
  * - Dispatches route update actions to navigate to the personal details page.
  *
  * @returns {void}
  */
  const selectedUser = useSelector((state) => state.user.selectedUser)
  const handleProfileClick = () => {

    const userData = JSON.parse(localStorage.getItem('userData'));
    if (userData?.userId === selectedUser?.userId) {
      sessionStorage.setItem('routeStatus', '/userPersonalDetails');
      dispatch(setRouteStatus('/userPersonalDetails'));
      dispatch(setUsrDetailsRoute('/personaldetails'));
      return
    }
          dispatch(setRouteStatus('/userPersonalDetails'));
      dispatch(setUsrDetailsRoute('/personaldetails'));
    // store.dispatch({ type: 'RESET_STATE' });
    dispatch(setSelectedUser(userData));

  };

  return (
    <div className='INCSUBNavbar'>
      {/* Logo */}
      <div className='INCSUBNavbar_logo_container'>
        <img src='/assets/images/navbar/full_logo.png' alt='banner_logo' />
      </div>

      {/* Right side: Notification and User Profile */}
      <div className='INCSUBNavbar_profile_container'>

        {/* Notification bell */}
        <div
          className='INCSUBNotification_icon_container'
          onClick={() => setNotifiPanel(!notifiPanel)}
          ref={notificationIconRef}
        >
          <svg width="24" height="26" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd"
              d="M10.0277 0C7.73472 0 5.80843 1.72411 5.55522 4.00306L4.5 13.5H1.5C0.671573 13.5 0 14.1716 0 15V16.5C0 17.3284 0.671573 18 1.5 18H22.5C23.3284 18 24 17.3284 24 16.5V15C24 14.1716 23.3284 13.5 22.5 13.5H19.5L18.4448 4.00306C18.1916 1.72411 16.2653 0 13.9723 0H10.0277Z"
              fill="#4880FF"
            />
            <rect opacity="0.3" x="9" y="19.5" width="6" height="6" rx="2.25" fill="#FF0000" />
          </svg>

          {/* Notification Dropdown Panel */}
          {notifiPanel && (
            <div
              className='INCSUBNotificationPanel_container'
              onClick={(e) => e.stopPropagation()}
              ref={notifiRef}
            >
              <INCSUBNotification />
            </div>
          )}
        </div>

        {/* Profile Info */}
        <div className='INCSUBNavbar_profile_info_container' onClick={handleProfileClick}>
          <div className='INCSUBNavbar_profile_image_holder'>
            <img
              style={{ width: '40px' }}
              src={profileInfo?.userImage || '/assets/icons/common/defaultImage.png'}
              alt='profile_image'
            />
          </div>
          <div className='INCSUBProfile_info'>
            <h1>{profileInfo?.userFullName}</h1>
            <h3>{profileInfo?.userRole}</h3>
          </div>

          {/* Chevron Icon */}
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 19.25C15.1086 19.25 19.25 15.1086 19.25 10C19.25 4.89137 15.1086 0.75 10 0.75C4.89137 0.75 0.75 4.89137 0.75 10C0.75 15.1086 4.89137 19.25 10 19.25Z"
              stroke="#5C5C5C"
              strokeWidth="0.5"
            />
            <path
              d="M10 10.7929L7.73162 8.14645C7.56425 7.95118 7.29289 7.95118 7.12553 8.14645C6.95816 8.34171 6.95816 8.65829 7.12553 8.85355L9.69695 11.8536C9.86432 12.0488 10.1357 12.0488 10.303 11.8536L12.8745 8.85355C13.0418 8.65829 13.0418 8.34171 12.8745 8.14645C12.7071 7.95118 12.4358 7.95118 12.2684 8.14645L10 10.7929Z"
              fill="#565656"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default INCSUBNavbar;
